import {FormGroup, FormBuilder, Validators} from '@angular/forms';
import {filter, take, takeUntil, distinctUntilChanged, tap} from 'rxjs/operators';
import {GeospatialFilterStore} from '../api/geospatial-filter.store';
import {Layer, LayerType} from './layer';
import {GeospatialOutageEntitiesConfiguration} from '../model/viewer-config';
import {BucketDto} from './metric-layer';
import {OutageFilterControlsStore} from '../control-panel/components/filter/outage-layer-filter.store';
import {GeoJSONGeometry, SpatialEntityData} from '../model/api';

export interface OutageEntityDto extends SpatialEntityData {
    label: string;
    centroidGeoJsonStr?: string;
    centroidGeoJsonGeometry?: GeoJSONGeometry;
}

export interface OutageDto {
    key: string;
    label: string;
    disabled: boolean;
    buckets: BucketDto[];
}

export interface OutageGroupDto {
    groupKey: string;
    groupLabel: string;
    metrics: OutageDto[];
}

export enum OutageLabelFormatType {
    PERCENTAGE = 'PERCENTAGE',
    ABSOLUTE = 'ABSOLUTE',
    BOTH = 'BOTH',
    NONE = 'NONE',
}

export const OutageLabelFormatLabels = {
    PERCENTAGE: 'Percentage (of total outages)',
    ABSOLUTE: 'Absolute Values',
};

export class OutageLayer extends Layer {
    mapData: OutageEntityDto[] = [];
    outageMetric: OutageDto;

    layerForm: FormGroup;
    filterControlsStore: OutageFilterControlsStore;

    private resetCategories: boolean = false;

    static defaultName: string = 'New Outage Layer';
    constructor(public fb: FormBuilder, public geospatialFilterStore: GeospatialFilterStore) {
        super(OutageLayer.defaultName, LayerType.OUTAGE, fb, geospatialFilterStore);
        this.outageMetric = {
            key: '',
            label: '',
            disabled: false,
            buckets: [],
        };

        // Create a new OutageFilterControlsStore instance
        this.filterControlsStore = new OutageFilterControlsStore(fb, geospatialFilterStore);

        // Initialize the form
        this.initForm();

        // Note: this.subscribeToFormChanges() is called from within initForm
    }

    initForm(): void {
        this.layerForm = this.fb.group({
            visible: [true],
            name: [this.name, Validators.required],
            outageMetric: [null, Validators.required], // type: OutageDto
            viewBy: [null, Validators.required],
            labelFormat: [
                [
                    {
                        name: OutageLabelFormatLabels[OutageLabelFormatType.ABSOLUTE],
                        key: OutageLabelFormatType.ABSOLUTE,
                        selected: true,
                        enabled: true,
                    },
                    {
                        name: OutageLabelFormatLabels[OutageLabelFormatType.PERCENTAGE],
                        key: OutageLabelFormatType.PERCENTAGE,
                        selected: false,
                        enabled: true,
                    },
                ],
                Validators.required,
            ],
            categoriesSelected: [null],
        });

        // Merge with FilterControlsStore's formGroup (filters)
        // Check if the filters form group is ready before adding it
        const filtersFormGroup = this.filterControlsStore.filtersFormGroup;

        // Add the filterGroups form group only if it exists
        if (filtersFormGroup) {
            this.layerForm.addControl('filters', filtersFormGroup);
            this.subscribeToFormChanges();
        } else {
            // If filterGroupsFormGroup is not ready yet, wait until it is initialized
            this.filterControlsStore.filtersFormGroupChanged$
                .pipe(
                    tap(() => this.filterLoading.next(true)),
                    filter((initialised) => !!initialised),
                    take(1),
                )
                .subscribe(() => {
                    this.layerForm.addControl('filters', this.filterControlsStore.filtersFormGroup, {
                        emitEvent: false,
                    });

                    this.subscribeToFormChanges();
                    this.filterLoading.next(false);
                });
        }
    }

    updateName(newName: string) {
        this.name = newName;
        this.layerForm.get('name').setValue(newName);
    }

    toggleVisibility(): void {
        this.visible = !this.visible;
        this.layerForm.get('visible').setValue(this.visible);
    }

    subscribeToFormChanges(): void {
        this.layerForm
            .get('outageMetric')
            .valueChanges.pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter((value) => !!value),
            )
            .subscribe((metric: OutageDto) => {
                this.updateName(this.configureLayerName(metric));
                this.outageMetric = metric;

                // set flag to notify updateMapDataSource() to reset the categories on reload
                this.resetCategories = true;
            });

        this.layerForm
            .get('filters')
            .valueChanges.pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                filter((value) => !!value),
            )
            .subscribe(() => {
                // set flag to notify updateMapDataSource() to reset the categories on reload
                this.resetCategories = true;
            });
    }

    public configureLayerName(metric?: OutageDto) {
        if (!metric) return OutageLayer.defaultName;
        return `Outage Layer: ${metric.label}`;
    }

    getFilters() {
        return this.filterControlsStore.currentFilters;
    }

    getOutageMetric() {
        return this.layerForm.value.outageMetric?.key;
    }

    getOutageViewBy() {
        return this.layerForm.value.viewBy?.value;
    }

    getLabelFormat() {
        const selectedLabels = this.layerForm.value.labelFormat.filter((l) => l.selected);

        if (selectedLabels.length == 0) return OutageLabelFormatType.NONE;
        if (selectedLabels.length == 1) return selectedLabels[0].key;
        if (selectedLabels.length == 2) return OutageLabelFormatType.BOTH;
    }

    updateMapDataSource(mapDataSource: GeospatialOutageEntitiesConfiguration) {
        this.mapData = mapDataSource.data;

        // On a reload, if a value has changed that should prompt the buckets to be reset, reset them.
        if (this.resetCategories) {
            const newMetric: OutageDto = {
                ...this.outageMetric,
                buckets: mapDataSource.buckets,
            };
            this.outageMetric = newMetric;
            // Note: DO NOT update this.layerForm.get('outageMetric'); this is unnecessary, and will cause the option
            // in the control panel to change due to new buckets.
            this.updateCategoriesSelectedSilent(newMetric.buckets);
            this.resetCategories = false;
        }
    }

    updateCategoriesSelectedSilent(buckets: BucketDto[]) {
        this.layerForm.get('categoriesSelected').setValue(buckets, {emitEvent: false});
    }

    getBuckets(categoriesSelected = this.layerForm.value.categoriesSelected): string[] {
        const arr: string[] = [];
        categoriesSelected?.forEach((bucket: BucketDto) => {
            arr.push(bucket.key);
        });
        return arr;
    }

    showCategorySection(): boolean {
        return this.outageMetric.buckets?.length > 0;
    }

    clearFilters(): void {
        this.filterControlsStore.resetGeospatialFilters();
    }

    layerCanBeDisplayed(): boolean {
        return this.getOutageViewBy() && this.getOutageMetric() && super.layerCanBeDisplayed();
    }

    copy(layer: OutageLayer): void {
        super.copy(layer);
        // We need to ensure the layerForm is initialized before performing an operation on it
        this.filterLoading$
            .pipe(
                filter((loading) => !loading),
                take(1),
            )
            .subscribe(() => {
                this.layerForm.patchValue(layer.layerForm.value);
                this.outageMetric = layer.outageMetric;
            });
    }
}
